import {
  DatePicker,
  DayOfWeek,
  IDatePickerProps,
  Stack,
} from '@fluentui/react';
import {
  dateFormat,
  onFormatDate,
  validateFutureDate,
} from 'common/utils/dateFormats';
import React, { useState, useCallback } from 'react';
import * as Yup from 'yup';

interface FutureDatePickerProps extends IDatePickerProps {
  onSelectDate: (date: Date | null | undefined) => void;
  onValidationError: (hasError: boolean, errorMessage?: string) => void;
  label?: string;
  customPlaceholder?:string
}

const validationSchema = Yup.string()
  .required('Date is required')
  .test('is-future', 'Date must be in the future', (value) => {
    if (value) {
      const parsedDate = new Date(dateFormat(value));
      return validateFutureDate(parsedDate);
    }
    return false;
  });

const parseDateFromString = (text: string): Date => {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1; // Months are zero-based, so add 1

  // Regex to match the date format "M/d/yyyy" or "M/d"
  const regex = /^(\d{1,2})\/(\d{1,2})(?:\/(\d{4}))?$/;
  const match = text.match(regex);

  if (match) {
    // Extract the month, day, and year
    const month = match[1]; // Month as string
    const day = match[2]; // Day as string
    const year = match[3]; // Year as string (optional)

    // Convert the month, day, and year to numbers
    const parsedMonth = month ? parseInt(month, 10) : currentMonth;
    const parsedDay = day ? parseInt(day, 10) : 1;
    const parsedYear = year ? parseInt(year, 10) : currentYear;

    // Return the Date object
    return new Date(parsedYear, parsedMonth - 1, parsedDay); // Month is 0-indexed
  }

  // If no valid date is found, return the current date with 1st day of the current month
  return new Date(currentYear, currentMonth - 1, 1);
};

export const FutureDatePicker: React.FC<FutureDatePickerProps> = ({
  onSelectDate,
  onValidationError,
  label,
  customPlaceholder,
}) => {
  const [errorState, setErrorState] = useState<string | string[]>([]);
  const [formattedDate, setFormattedDate] = useState<Date | undefined>(
    undefined
  );

  const handleTextChange = useCallback(
    (newValue: string | undefined) => {
      if (newValue) {
        validationSchema
          .validate(newValue)
          .then(() => {
            setErrorState([]);
            onValidationError(false);
          })
          .catch((err) => {
            setErrorState([err.message]);
            onValidationError(true, err.message);
          });
      } else {
        setErrorState([]);
        onValidationError(false);
      }
    },
    [onValidationError]
  );

  const handleDateSelection = (date: Date | null | undefined) => {
    setErrorState([]);
    onValidationError(false);
    if (date) {
      onSelectDate(date);
    } else {
      onSelectDate(undefined); // explicitly handle undefined
    }
  };

  const handleParseDateFromString = (text: string): Date | null => {
    const parsedDate = parseDateFromString(text);
    setFormattedDate(parsedDate);
    handleTextChange(parsedDate.toString());
    return parsedDate;
  };

  return (
    <Stack horizontal verticalAlign="end" tokens={{ childrenGap: 4 }}>
      <Stack.Item grow={1}>
        <DatePicker
          label={label ? label : undefined}
          minDate={new Date()}
          value={formattedDate}
          firstDayOfWeek={DayOfWeek.Sunday}
          placeholder={customPlaceholder??"Finalized by date (optional)"}
          ariaLabel="Date"
          formatDate={onFormatDate}
          showWeekNumbers
          firstWeekOfYear={1}
          showMonthPickerAsOverlay
          showGoToToday={false}
          onSelectDate={handleDateSelection}
          textField={{
            errorMessage:
              Array.isArray(errorState) && errorState.length > 0
                ? errorState[0]
                : '',
          }}
          parseDateFromString={handleParseDateFromString}
          allowTextInput
        />
      </Stack.Item>
    </Stack>
  );
};
